import React, { useEffect, useState } from 'react';
import '../../styles/Tables.css';
import { useTranslation } from 'react-i18next';

function HoldingsTable({ holdings, currentLanguage }) {
  const { t } = useTranslation();
  const [forceRefresh, setForceRefresh] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterText, setFilterText] = useState('');
  
  // Force re-render when language changes (kept for future translation support)
  useEffect(() => {
    setForceRefresh(prev => prev + 1);
  }, [currentLanguage]);
  
  // Format numbers according to locale (always English for now)
  const formatNumber = (num) => {
    try {
      return new Intl.NumberFormat('en-US').format(num);
    } catch (err) {
      return num.toString();
    }
  };
  
  // Sorting function
  const sortData = (data, key, direction) => {
    return [...data].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };
  
  // Handle sort
  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };
  
  // Filter data
  const filteredData = holdings.filter(holding => 
    holding.symbol.toLowerCase().includes(filterText.toLowerCase())
  );
  
  // Sort filtered data
  const sortedData = sortConfig.key ? sortData(filteredData, sortConfig.key, sortConfig.direction) : filteredData;
  
  // Calculate stats
  const totalHoldings = sortedData.length;
  const longPositions = sortedData.filter(h => h.quantity > 0).length;
  const shortPositions = sortedData.filter(h => h.quantity < 0).length;
  
  return (
    <div className="fancy-portfolio-container" key={`holdings-table-${currentLanguage}-${forceRefresh}`}>
      {/* Portfolio Header */}
      <div className="portfolio-header">
        <div className="header-content">
          <div className="portfolio-stats">
            <div className="stat-badge total">
              <span className="stat-number">{formatNumber(totalHoldings)}</span>
              <span className="stat-label">{t('portfolio.totalHoldings')}</span>
            </div>
            <div className="stat-badge long">
              <span className="stat-number">{formatNumber(longPositions)}</span>
              <span className="stat-label">{t('portfolio.long')}</span>
            </div>
            <div className="stat-badge short">
              <span className="stat-number">{formatNumber(shortPositions)}</span>
              <span className="stat-label">{t('portfolio.short')}</span>
            </div>
            <div className="search-badge">
              <div className="search-container">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  placeholder={t('portfolio.searchPlaceholder', 'Search symbols...')}
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  className="search-input"
                />
                {filterText && (
                  <button 
                    className="clear-search"
                    onClick={() => setFilterText('')}
                    title={t('portfolio.clearSearch', 'Clear search')}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Enhanced Table */}
      <div className="fancy-table-container">
        <table className="fancy-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('symbol')} className="sortable-header">
                <div className="header-content">
                  <span>{t('portfolio.symbol')}</span>
                  {sortConfig.key === 'symbol' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
              <th onClick={() => handleSort('quantity')} className="sortable-header">
                <div className="header-content">
                  <span>{t('portfolio.quantity')}</span>
                  {sortConfig.key === 'quantity' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
              <th onClick={() => handleSort('quantity')} className="sortable-header">
                <div className="header-content">
                  <span>{t('portfolio.position')}</span>
                  {sortConfig.key === 'quantity' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.length > 0 ? (
              sortedData.map((holding, index) => (
                <tr key={`${holding.symbol}-${index}-${currentLanguage}`} className="holding-row">
                  <td>
                    <div className="symbol-cell">
                      <div className="symbol-badge">
                        <span className="symbol-text">{holding.symbol}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="quantity-cell">
                      <span className="quantity-value">{formatNumber(Math.abs(holding.quantity))}</span>
                      <span className="quantity-unit">{t('portfolio.shares', 'shares')}</span>
                    </div>
                  </td>
                  <td>
                    <div className="position-cell">
                      <span className={`position-badge ${holding.quantity >= 0 ? 'long' : 'short'}`}>
                        <span className="position-icon">
                          {holding.quantity >= 0 ? '📈' : '📉'}
                        </span>
                        <span className="position-text">
                          {holding.quantity >= 0 ? t('portfolio.long') : t('portfolio.short')}
                        </span>
                      </span>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr className="empty-state">
                <td colSpan="3">
                  <div className="empty-content">
                    <span className="empty-icon">📭</span>
                    <p className="empty-text">
                      {filterText 
                        ? t('portfolio.noResults', 'No symbols match your search')
                        : t('portfolio.noHoldings', 'No holdings available')
                      }
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Portfolio Footer */}
      <div className="portfolio-footer">
        <div className="footer-info">
          <span className="footer-text">
            {t('portfolio.lastUpdated', 'Last Updated')}: {new Date().toLocaleString()}
          </span>
        </div>
        <div className="footer-actions">
          <button className="footer-btn" onClick={() => window.location.reload()}>
            <span className="btn-icon">🔄</span>
            <span className="btn-text">{t('portfolio.refresh', 'Refresh')}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default HoldingsTable; 